import { createFifoCache } from '@ardoq/common-helpers';
import {
  addAncestorAndDescendantReferences,
  getAllReferences,
  getFilterByReferenceTypeAndGlobalFilters,
  getWithoutFilter,
  joinSets,
  mapToDictionaryList,
  traverse,
} from './traversalUtils';
import { ArdoqId } from '@ardoq/api-types';
import { GraphInterface } from '../graphInterface/graphInterface';
import { AggregatedReferenceType, GraphModelShape } from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-interface';
import { TraverseOptions } from '../traversal/types';
import { range } from 'lodash';

export const traverseGraph = (
  graphInterface: GraphInterface,
  startSet: ArdoqId[],
  graph: GraphModelShape,
  traverseOptions: TraverseOptions,
  ctx: ReferenceFilterContext
): {
  componentIds: ArdoqId[];
  referenceIds: ArdoqId[];
  traversedReferenceIds: ArdoqId[];
  parentReferenceIds: Set<string>;
  traversedOutgoingReferenceTypes: AggregatedReferenceType[];
  traversedIncomingReferenceTypes: AggregatedReferenceType[];
} => {
  const {
    sourceMap,
    targetMap,
    referenceMap,
    childrenByParent,
    componentsById,
  } = graph;

  const {
    maxDegreesIncoming,
    maxDegreesOutgoing,
    maxDegrees,
    isParentRelationAsReference,
    incomingReferenceTypes,
    outgoingReferenceTypes,
    disableAllFilters,
    excludedComponentIds,
  } = traverseOptions;
  const filteredStartSet = startSet.filter(componentId => {
    const component = graphInterface.get(componentId);
    return (
      !component ||
      graphInterface.isComponentIncludedInContextByFilter(component, ctx)
    );
  });
  const traversedReferenceIds = new Set<ArdoqId>();
  const traversedOutgoingIds = new Set<ArdoqId>();
  const traversedIncomingIds = new Set<ArdoqId>();

  const {
    sourceMap: extendedSourceMap,
    targetMap: extendedTargetMap,
    parentReferenceIds,
  } = isParentRelationAsReference
    ? addAncestorAndDescendantReferences(
        { childrenByParent, componentsById, excludedComponentIds },
        sourceMap,
        targetMap,
        filteredStartSet
      )
    : { sourceMap, targetMap, parentReferenceIds: new Set<string>() };

  const filterOutgoing = createFifoCache(
    Number.POSITIVE_INFINITY,
    disableAllFilters
      ? getWithoutFilter(traversedOutgoingIds)
      : getFilterByReferenceTypeAndGlobalFilters(
          graphInterface,
          outgoingReferenceTypes,
          parentReferenceIds,
          traversedReferenceIds,
          ctx
        )
  );

  const filterIncoming = createFifoCache(
    Number.POSITIVE_INFINITY,
    disableAllFilters
      ? getWithoutFilter(traversedIncomingIds)
      : getFilterByReferenceTypeAndGlobalFilters(
          graphInterface,
          incomingReferenceTypes,
          parentReferenceIds,
          traversedReferenceIds,
          ctx
        )
  );

  const filteredStartSetTraversedMaxDegree = range(maxDegrees).reduce(
    (startSet: ArdoqId[]) => {
      const outgoingNodes = traverse(
        startSet,
        extendedSourceMap,
        1,
        filterOutgoing
      );
      const incomingNodes = traverse(
        startSet,
        extendedTargetMap,
        1,
        filterIncoming
      );
      return Array.from(joinSets(startSet, outgoingNodes, incomingNodes));
    },
    filteredStartSet
  );

  const outgoingNodes = traverse(
    filteredStartSetTraversedMaxDegree,
    extendedSourceMap,
    maxDegreesOutgoing,
    filterOutgoing
  );

  const incomingNodes = traverse(
    filteredStartSetTraversedMaxDegree,
    extendedTargetMap,
    maxDegreesIncoming,
    filterIncoming
  );

  const resultNodes = joinSets(
    filteredStartSetTraversedMaxDegree,
    outgoingNodes,
    incomingNodes
  );

  const referenceIds = getAllReferences(
    graphInterface,
    referenceMap,
    resultNodes,
    ctx
  );

  const traversedOutgoingReferenceTypes = mapToDictionaryList(
    graphInterface,
    traversedOutgoingIds
  );

  const traversedIncomingReferenceTypes = mapToDictionaryList(
    graphInterface,
    traversedIncomingIds
  );

  return {
    componentIds: Array.from(resultNodes),
    referenceIds,
    traversedReferenceIds: Array.from(traversedReferenceIds),
    parentReferenceIds,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
  };
};
